/** @format */
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { fallbackImagePath } from '@toucan/client/global/product';

import { CardBackground } from '@toucan/client/shared/components/card-background';
import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';

import { ProductPrice } from './components/product-price';
import { ProductWrappedLink } from './components/product-wrapped-link';
import { DEFAULT_TEST_ID } from './constants';
import {
    cardBackgroundStyles,
    miniProductCardStyles,
} from './mini-product-card.styles';
import { MiniProductCardProps } from './types';

const StyledMiniProductCard = styled.div`
    ${miniProductCardStyles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledCardBackground = styled(CardBackground)`
    ${cardBackgroundStyles}
`;

export const MiniProductCard: FunctionComponent<MiniProductCardProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    condition,
    imageUrl,
    isCurrentProduct,
    price,
    name,
    sku,
    urlSegment,
    ...otherProps
}) => {
    const { t } = useTranslation();

    const tag = isCurrentProduct ? t('This product') : undefined;

    return (
        <ProductWrappedLink
            data-testid={`${dataTestId}__wrapped-link`}
            isCurrentProduct={isCurrentProduct}
            sku={sku}
            urlSegment={urlSegment}
        >
            <StyledCardBackground
                data-testid={dataTestId}
                tag={tag}
                expandOnHover={!isCurrentProduct}
                {...otherProps}
            >
                <StyledMiniProductCard>
                    <Thumbnail
                        data-testid={`${dataTestId}__thumbnail`}
                        className="mini-product-card-thumbnail"
                        aspectRatio="4/3"
                        height="10rem"
                    >
                        <CloudflareImage
                            src={imageUrl}
                            fallbackSrc={fallbackImagePath}
                            alt={name}
                            imageSize={CloudflareImageSize.THUMBNAIL}
                        />
                    </Thumbnail>
                    <FormattedCondition
                        data-testid={`${dataTestId}__condition`}
                        className="mini-product-card-condition"
                        condition={condition}
                    />
                    <ProductPrice
                        data-testid={`${dataTestId}__price`}
                        className="mini-product-card-price"
                        listPrice={price / 100}
                        as="p"
                    />
                </StyledMiniProductCard>
            </StyledCardBackground>
        </ProductWrappedLink>
    );
};
